/**
 * The look card (RC2-07) — what a thing is, and what it is worth.
 *
 * Examining used to be one more line scrolling past in the chronicle, which is
 * the wrong shape for it twice over: the chronicle is a *record* and a look is a
 * *question*, and a player arrowing the cursor across six tiles buried the four
 * lines they had just read under six more. So the answer becomes a card, and the
 * chronicle keeps only what happened.
 *
 * ## Two halves, from two places, on purpose
 *
 * - **The prose is the server's.** Descriptions are composed in `@annwn/sim`
 *   beside the content that defines them (M0-06) — the client has never held a
 *   table of what a tile reads like, and this does not give it one. The card
 *   prints `examined.text` and nothing else.
 * - **The subject is the snapshot's.** The name, the glyph and the quality chip
 *   come from the flora, actor and carcass the client is *already* being sent
 *   and *already* drawing. Nothing new goes on the wire for this card.
 *
 * ## The quality badge is a legibility win, not decoration
 *
 * RC1 put the fire odds on the kindle verb so a player could tell whether it was
 * worth the tinder before they spent it. `Prime` on a herb is the same move for
 * the same reason: `standing` has travelled on `FloraSnapshot` since M3-03 as
 * *the only feedback the scarcity rule ever gives*, and until now the only way
 * to read it was to notice that the glyph had changed from `*` to `,`. A forager
 * deciding whether to kneel down should be told before they commit.
 */

import { GLYPH_CODEPOINTS } from '@annwn/content';
import type { CarcassSnapshot, EntitySnapshot, FloraSnapshot } from '@annwn/protocol';
import type { BadgeTone, MenuItem } from '@annwn/ui';

import { ACTOR_LOOK, CURSOR_LOOK, FLORA_LOOK } from '../render/palette.js';

/**
 * The character an atlas glyph index draws.
 *
 * Read back out of the font table rather than restated, so the card and the grid
 * cannot show a thing as two different marks. `FLORA_LOOK.picked.glyph` is a
 * decision about how a stripped patch looks, and this card is obliged to honour
 * it rather than to have an opinion.
 */
export function charOfGlyph(index: number): string {
  const codepoint = GLYPH_CODEPOINTS[index];
  return codepoint === undefined || codepoint === 0 ? ' ' : String.fromCodePoint(codepoint);
}

export interface ExamineBadge {
  readonly label: string;
  readonly tone: BadgeTone;
}

export interface ExamineCard {
  /** Where the cursor was. Held so a stale answer can be dropped. */
  readonly x: number;
  readonly y: number;

  /** What the card is about: `flora`, `actor`, `carcass`, or `ground`. */
  readonly kind: string;

  /** The name at the top. Title-cased for the heading, never for the prose. */
  readonly title: string;

  /** The mark the grid draws for it, so the card and the map agree. */
  readonly glyph: string;

  /** Its worth, at a glance — or `null` for a thing that has no grade. */
  readonly badge: ExamineBadge | null;

  /** The server's description, one paragraph per sentence it composed. */
  readonly lines: readonly string[];

  /** What you may do with it from here, with the keys that do it. */
  readonly verbs: readonly MenuItem[];
}

/** What the cursor is over — everything the client can already see there. */
export interface ExamineSubject {
  readonly x: number;
  readonly y: number;
  readonly flora?: FloraSnapshot | undefined;
  readonly actor?: EntitySnapshot | undefined;
  readonly carcass?: CarcassSnapshot | undefined;
  /** The tile kind the client last had for this square, e.g. `meadow`. */
  readonly tile?: string | undefined;
  /** True when the cursor is over the viewer's own square. */
  readonly own?: boolean;
}

/**
 * A patch's standing, as the word a forager wants.
 *
 * `Prime` rather than "thick" because the question is not how dense the plants
 * are, it is whether this is worth stopping for — and `Bare` rather than
 * "picked-over" because it has to be readable at chip size.
 */
const STANDING_BADGE: Readonly<Record<string, ExamineBadge>> = {
  thick: { label: 'Prime', tone: 'life' },
  thin: { label: 'Thinned', tone: 'warn' },
  'picked-over': { label: 'Bare', tone: 'neutral' },
};

/** A carcass's stage, on the same principle: how much meat is still in it. */
const STAGE_BADGE: Readonly<Record<string, ExamineBadge>> = {
  fresh: { label: 'Fresh', tone: 'life' },
  cooling: { label: 'Cooling', tone: 'life' },
  turning: { label: 'Turning', tone: 'warn' },
  rotten: { label: 'Rotten', tone: 'bleed' },
};

/**
 * What a body is, at a glance.
 *
 * `Helpless` and not "dead": you cannot tell across a clearing whether the man
 * face-down in the mud is breathing, and the card must not claim to know what
 * the glyph deliberately does not say (M2-07).
 */
function actorBadge(actor: EntitySnapshot): ExamineBadge | null {
  if (actor.combat?.down) return { label: 'Helpless', tone: 'bleed' };
  if (actor.pale) return { label: 'Grey-streaked', tone: 'myth' };
  if (actor.combat?.guarding) return { label: 'Guard up', tone: 'warn' };
  return null;
}

/** `hazel-wood` → `Hazel wood`. The tile kind is the only name the client has. */
function tileTitle(tile: string | undefined): string {
  if (!tile) return 'The ground';
  const words = tile.replace(/[-_]/g, ' ');
  return words.slice(0, 1).toUpperCase() + words.slice(1);
}

function titleCase(name: string): string {
  return name.slice(0, 1).toUpperCase() + name.slice(1);
}

/**
 * The verbs the card offers, with the keys that run them.
 *
 * A short list on purpose. This is the card you read *before* committing, so it
 * carries only what applies to the thing you are pointed at — everything you can
 * do where you stand is one key away in the camp menu, and duplicating it here
 * would make the card a second, subtly different action menu.
 */
function verbsFor(subject: ExamineSubject): MenuItem[] {
  const verbs: MenuItem[] = [];

  // Gathering is a thing you do to the ground under you, so it is offered only
  // when the cursor is on your own square — the same rule `forage` itself keeps.
  if (subject.flora && subject.own) {
    verbs.push(
      subject.flora.standing === 'picked-over'
        ? { id: 'forage', key: 'f', name: 'gather', disabled: true, disabledReason: 'picked bare' }
        : { id: 'forage', key: 'f', name: 'gather' },
    );
  }

  if (subject.actor && !subject.own) {
    verbs.push({ id: 'target', key: 't', name: 'mark as target' });
  }

  if (subject.carcass) {
    verbs.push({ id: 'camp', key: 'c', name: 'butcher…', meta: 'camp menu' });
  }

  return verbs;
}

/**
 * Build the card from what the cursor is over and what the server answered.
 *
 * `lines` arrive as the server's composed description, split on the newlines it
 * joined them with. Returned rather than rendered, so "the card never invents a
 * description" is a rule a test can check without a DOM.
 */
export function examineCard(subject: ExamineSubject, description: string): ExamineCard {
  const lines = description
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

  const base = { x: subject.x, y: subject.y, lines, verbs: verbsFor(subject) };

  // Most specific first, and the order is the one the describer itself uses:
  // a person standing on a patch of yarrow is what the card is about.
  if (subject.actor) {
    return {
      ...base,
      kind: 'actor',
      title: titleCase(subject.actor.name),
      glyph: charOfGlyph(
        subject.actor.combat?.down ? ACTOR_LOOK.down.glyph : ACTOR_LOOK.player.glyph,
      ),
      badge: actorBadge(subject.actor),
    };
  }

  if (subject.carcass) {
    return {
      ...base,
      kind: 'carcass',
      title: titleCase(subject.carcass.name),
      glyph: charOfGlyph(ACTOR_LOOK.carcass.glyph),
      badge: STAGE_BADGE[subject.carcass.stage] ?? null,
    };
  }

  if (subject.flora) {
    const picked = subject.flora.standing === 'picked-over';
    return {
      ...base,
      kind: 'flora',
      title: titleCase(subject.flora.name),
      glyph: charOfGlyph(picked ? FLORA_LOOK.picked.glyph : FLORA_LOOK.standing.glyph),
      badge: STANDING_BADGE[subject.flora.standing] ?? null,
    };
  }

  return {
    ...base,
    kind: 'ground',
    title: tileTitle(subject.tile),
    glyph: charOfGlyph(CURSOR_LOOK.glyph),
    badge: null,
  };
}
